import { Currency } from '@seapoint/server/enums';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  END_DATE_FILTER_PARAM_NAME,
  START_DATE_FILTER_PARAM_NAME,
  VENDOR_FILTER_PARAM_NAME,
} from '../../../../design-system/data-table/data-table';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { MoneyMovementChart } from '../../components/charts/money-movement-chart';
import { MoneyMovementFilter, type MoneyMovementRange } from '../../components/charts/money-movement-filter';
import { TopInOutVendorList } from '../../components/charts/top-in-out-vendor-list';
import { getMoneyMovementData, type TimePeriod } from '../../utils/get-money-movement-data';
import { getTopMoneyMovementSources } from '../../utils/get-top-money-movement-sources';

const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly',
};

const TIME_PERIODS_BY_RANGE: Record<MoneyMovementRange, TimePeriod[]> = {
  current_month: ['day', 'week', 'month'],
  last_month: ['day', 'week', 'month'],
  last_3_months: ['day', 'week', 'month'],
  last_6_months: ['day', 'week', 'month', 'quarter'],
  last_year: ['day', 'week', 'month', 'quarter', 'year'],
};

const buildTransactionTableUrl = (startDate: Date, endDate: Date, vendorNames: string[]) => {
  const params = new URLSearchParams();
  params.set(START_DATE_FILTER_PARAM_NAME, JSON.stringify(startDate.getTime()));
  params.set(END_DATE_FILTER_PARAM_NAME, JSON.stringify(endDate.getTime()));
  if (vendorNames.length > 0) {
    params.set(VENDOR_FILTER_PARAM_NAME, JSON.stringify(vendorNames));
  }
  return `/dashboard/transactions?${params.toString()}`;
};

type MoneyMovementChartWrapperProps = {
  accounts: AccountView[];
  baseCurrency: Currency;
};
export const MoneyMovementChartWrapper = ({ accounts, baseCurrency }: MoneyMovementChartWrapperProps) => {
  const navigate = useNavigate();
  const [moneyInView, setMoneyInView] = useState<'amount' | 'percent'>('amount');
  const [moneyOutView, setMoneyOutView] = useState<'amount' | 'percent'>('amount');
  const [visibleSeries, setVisibleSeries] = useState<'both' | 'in' | 'out'>('both');
  const [range, setRange] = useState<MoneyMovementRange>('last_month');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TIME_PERIODS_BY_RANGE['last_month'][0]);

  const now = new Date();

  const handleRangeChange = (nextRange: MoneyMovementRange) => {
    setRange(nextRange);
    const allowedTimePeriods = TIME_PERIODS_BY_RANGE[nextRange];
    setTimePeriod((current) => (allowedTimePeriods.includes(current) ? current : allowedTimePeriods[0]));
  };

  const computeStartDate = () => {
    switch (range) {
      case 'current_month':
        return startOfMonth(now);
      case 'last_month':
        return subMonths(startOfMonth(now), 1);
      case 'last_3_months':
        return subMonths(startOfMonth(now), 3);
      case 'last_6_months':
        return subMonths(startOfMonth(now), 6);
      case 'last_year':
        return subMonths(startOfMonth(now), 12);
      default:
        return exhaustiveCheck(range);
    }
  };

  const computeEndDate = () => {
    switch (range) {
      case 'current_month':
        return now; // Include today
      case 'last_month':
        return endOfMonth(subMonths(now, 1)); // End of previous month
      case 'last_3_months':
      case 'last_6_months':
      case 'last_year':
        return now;
      default:
        return exhaustiveCheck(range);
    }
  };

  const startDate = computeStartDate();
  const endDate = computeEndDate();
  const { topIn, topOut, comparison } = getTopMoneyMovementSources({
    accounts,
    startDate,
    endDate,
    range,
    baseCurrency,
  });

  const data = getMoneyMovementData({
    accounts,
    baseCurrency,
    startDate,
    endDate,
    timePeriod,
  });

  // Calculate totals
  const totalMoneyIn = data.reduce((acc, curr) => acc + curr.moneyIn, 0);
  const totalMoneyOut = data.reduce((acc, curr) => acc + curr.moneyOut, 0);

  // Format totals with currency
  const formattedMoneyIn = getAmountWithCurrency({
    amount: totalMoneyIn,
    currency: baseCurrency as SupportedCountryCurrency,
    isAccountBalance: true,
  });

  const formattedMoneyOut = getAmountWithCurrency({
    amount: totalMoneyOut,
    currency: baseCurrency as SupportedCountryCurrency,
    isAccountBalance: true,
  });

  // Filter data based on selected visibility
  const filteredData = data.map((item) => ({
    ...item,
    moneyIn: visibleSeries === 'out' ? 0 : item.moneyIn,
    moneyOut: visibleSeries === 'in' ? 0 : item.moneyOut,
  }));

  return (
    <Stack className="w-full" space={4}>
      <Inline justify="between" className="w-full">
        <h2 className="text-xl font-medium">Money Movement</h2>
        <MoneyMovementFilter value={range} onChange={handleRangeChange} />
      </Inline>
      <div className="w-full rounded-[10px] border-[0.5px] border-seapoint-gray-border px-4 pb-4 pt-4">
        <Stack space={10}>
          <Inline className="w-full px-4" justify="between" space={4} align="start">
            <Inline space={4}>
              <Stack space={1}>
                <p className="text-xs text-seapoint-gray-text">Money In</p>
                <p className="text-xl font-medium text-seapoint-green">{formattedMoneyIn}</p>
              </Stack>
              <Stack space={1}>
                <p className="text-xs text-seapoint-gray-text">Money Out</p>
                <p className="text-xl font-medium">-{formattedMoneyOut}</p>
              </Stack>
            </Inline>
            <Inline
              space={1.5}
              align="center"
              justify="end"
              wrap="wrap"
              className="mt-1 w-full gap-y-2 sm:w-auto sm:gap-y-0"
            >
              <Select
                value={timePeriod}
                onValueChange={(value) => setTimePeriod(value as TimePeriod)}
                placeholder="Time Scale"
                triggerClassName="h-8 w-full min-w-[140px] px-2 text-xs sm:w-auto sm:px-3 sm:text-sm"
                selectedTriggerContent={
                  <span className="truncate text-xs font-medium sm:text-sm">{TIME_PERIOD_LABELS[timePeriod]}</span>
                }
              >
                {TIME_PERIODS_BY_RANGE[range].map((period) => (
                  <Select.Item key={period} value={period} className="text-xs sm:text-sm">
                    {TIME_PERIOD_LABELS[period]}
                  </Select.Item>
                ))}
              </Select>

              <Select
                value={visibleSeries}
                onValueChange={(value) => setVisibleSeries(value as 'both' | 'in' | 'out')}
                placeholder="Show"
                triggerClassName="h-8 w-full min-w-[140px] px-2 text-xs sm:w-auto sm:px-3 sm:text-sm"
                selectedTriggerContent={
                  visibleSeries === 'both' ? (
                    <span className="truncate text-xs font-medium sm:text-sm">Money In and Out</span>
                  ) : (
                    <span className="truncate text-xs font-medium sm:text-sm">
                      {visibleSeries === 'in' ? 'Money In' : 'Money Out'}
                    </span>
                  )
                }
              >
                <Select.Item value="both">
                  <span className="text-xs sm:text-sm">Money In and Out</span>
                </Select.Item>
                <Select.Item value="in">
                  <span className="text-xs sm:text-sm">Money In</span>
                </Select.Item>
                <Select.Item value="out">
                  <span className="text-xs sm:text-sm">Money Out</span>
                </Select.Item>
              </Select>
            </Inline>
          </Inline>

          <MoneyMovementChart data={filteredData} baseCurrency={baseCurrency} height={200} />
        </Stack>
      </div>

      <div className="grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2">
        <TopInOutVendorList
          className="h-full"
          title="Top money in sources"
          currency={baseCurrency}
          vendors={topIn.map((s) => ({
            id: s.id,
            name: s.name,
            amount: s.amount,
            percent: s.percent,
            avatarUrl: s.avatarUrl,
          }))}
          emptyText="No incoming sources in this timeframe"
          view={moneyInView}
          changeView={setMoneyInView}
          footerAction={{
            visible: true,
            icon: <ArrowRight />,
            label: 'View all',
            onClick: () => {
              const vendorNames = topIn.map((vendor) => vendor.name);
              navigate(buildTransactionTableUrl(startDate, endDate, vendorNames));
            },
          }}
          numberOfVendors={5}
          comparison={comparison.topIn}
        />
        <TopInOutVendorList
          className="h-full"
          title="Top money out sources"
          currency={baseCurrency}
          vendors={topOut.map((s) => ({
            id: s.id,
            name: s.name,
            amount: s.amount,
            percent: s.percent,
            avatarUrl: s.avatarUrl,
          }))}
          emptyText="No outgoing sources in this timeframe"
          view={moneyOutView}
          changeView={setMoneyOutView}
          footerAction={{
            visible: true,
            icon: <ArrowRight />,
            label: 'View all',
            onClick: () => {
              const vendorNames = topOut.map((vendor) => vendor.name);
              navigate(buildTransactionTableUrl(startDate, endDate, vendorNames));
            },
          }}
          numberOfVendors={5}
          comparison={comparison.topOut}
        />
      </div>
    </Stack>
  );
};
